/**
 * One language for all three apps (landing, auth, logistics).
 *
 * Each app lives on its own address, and a browser keeps `localStorage` per address, so a language
 * chosen on the landing page was invisible to the logistics app and the other way round. Two things
 * carry the choice across, and either is enough on its own:
 *
 * - a **cookie** on the parent domain. In staging and production the apps are siblings
 *   (`auth.`, `logistics.` and the landing page under one name), so a cookie set for that name is
 *   read by all of them. This is the normal path.
 * - a **`?lang=` parameter** added to links and redirects that leave for another app. Local
 *   development uses unrelated hosts (`3asass.local`, `auth.local`, `logistics.local`) that share
 *   no parent, so no cookie can span them; the parameter is what keeps them together there. It is
 *   read once at start-up and taken out of the address bar again.
 *
 * Order of authority when an app starts: the address (the person just came from a link that said
 * so) > the shared cookie > this app's own storage > the browser's language.
 *
 * This file is identical in the three apps (they are separate packages with no shared library):
 * change one, change all three. Everything that touches the page takes its inputs as arguments, so
 * the rules are tested without a browser (`tests/shared-locale.test.mjs` in the logistics app).
 */

export const LOCALE_COOKIE = 'app.locale'
export const LOCALE_PARAM = 'lang'

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

/** The first label of a hostname that names an app, not the site: `auth.example.com` is `example.com`. */
const APP_LABELS = new Set(['auth', 'logistics', 'landing'])

/**
 * The domain a cookie must be set for so that every app can read it, or null when there is no such
 * domain (local hosts, an IP address, a single-label name): then the cookie stays on this host and
 * the `?lang=` parameter does the carrying.
 *
 * `VITE_COOKIE_DOMAIN` overrides the guess for a deployment whose names do not follow the pattern.
 */
export function sharedCookieDomain(hostname: string, override?: string | null): string | null {
  const forced = override?.trim().replace(/^\./, '')

  if (forced) return forced

  const host = hostname.trim().toLowerCase()

  if (!host.includes('.') || /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(':')) return null
  if (host.endsWith('.local') || host.endsWith('.localhost') || host.endsWith('.test')) return null

  const labels = host.split('.')
  const domain = APP_LABELS.has(labels[0]) && labels.length > 2 ? labels.slice(1).join('.') : host

  // A bare public suffix cannot be a cookie domain; two labels is the least a real site has.
  return domain.split('.').length >= 2 ? domain : null
}

/** The value of one cookie from a `document.cookie` string. */
export function readCookie(cookieString: string, name: string): string | null {
  for (const part of cookieString.split(';')) {
    const [key, ...rest] = part.trim().split('=')

    if (key === name) return decodeURIComponent(rest.join('='))
  }

  return null
}

/** The `Set-Cookie` text for the language, ready to assign to `document.cookie`. */
export function localeCookieText(locale: string, hostname: string, secure: boolean, override?: string | null): string {
  const domain = sharedCookieDomain(hostname, override)

  return [
    `${LOCALE_COOKIE}=${encodeURIComponent(locale)}`,
    'path=/',
    `max-age=${ONE_YEAR_SECONDS}`,
    'SameSite=Lax',
    ...(domain ? [`domain=${domain}`] : []),
    ...(secure ? ['Secure'] : [])
  ].join('; ')
}

/** The language named in an address, when it is one of `supported`. */
export function localeFromSearch(search: string, supported: readonly string[]): string | null {
  const value = new URLSearchParams(search).get(LOCALE_PARAM)

  return value && supported.includes(value) ? value : null
}

/** The address without the language parameter, so it does not stay in the bar, history or bookmarks. */
export function withoutLocaleParam(href: string): string {
  const url = new URL(href)
  url.searchParams.delete(LOCALE_PARAM)

  return url.toString()
}

/**
 * `target` with the language added, but only when it leaves for another origin: a link inside the
 * same app already shares its storage. A target that cannot be read as an address is returned as it
 * was.
 */
export function withLocale(target: string, locale: string, currentOrigin: string): string {
  try {
    const url = new URL(target, currentOrigin)

    if (url.origin === currentOrigin) return target

    url.searchParams.set(LOCALE_PARAM, locale)

    return url.toString()
  } catch {
    return target
  }
}

/** The browser half: read, write and carry the language. Nothing here runs unless it is called. */
export function readSharedLocale(): string | null {
  try {
    return readCookie(document.cookie, LOCALE_COOKIE)
  } catch {
    return null
  }
}

export function writeSharedLocale(locale: string): void {
  try {
    const override = (import.meta.env.VITE_COOKIE_DOMAIN as string | undefined) ?? null

    document.cookie = localeCookieText(locale, window.location.hostname, window.location.protocol === 'https:', override)
  } catch {
    // Cookies blocked: this app's own storage still remembers the choice.
  }
}

/**
 * The language in the address, taken out of the address bar as it is read. Called once, at start-up,
 * before the router looks at the address.
 */
export function takeLocaleFromAddress(supported: readonly string[]): string | null {
  try {
    const locale = localeFromSearch(window.location.search, supported)

    if (new URLSearchParams(window.location.search).has(LOCALE_PARAM)) {
      window.history.replaceState(window.history.state, '', withoutLocaleParam(window.location.href))
    }

    return locale
  } catch {
    return null
  }
}

/**
 * Add the current language to every link that leaves for one of the other apps, just before the
 * browser follows it (a click, or a middle click that opens a new tab). Links inside the app and
 * links to anywhere else are left alone.
 */
export function installLocaleLinkSync(origins: readonly string[], currentLocale: () => string): void {
  const known = new Set(
    origins.flatMap((origin) => {
      try {
        return [new URL(origin).origin]
      } catch {
        return []
      }
    })
  )

  const carry = (event: Event) => {
    const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null

    if (!anchor) return

    try {
      const url = new URL(anchor.href, window.location.href)

      if (url.origin === window.location.origin || !known.has(url.origin)) return

      anchor.href = withLocale(anchor.href, currentLocale(), window.location.origin)
    } catch {
      // A link that is not an address is not ours to change.
    }
  }

  document.addEventListener('click', carry, true)
  document.addEventListener('auxclick', carry, true)
}
